import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Buttons are 28px and 32px, never taller, because a dense page cannot afford a
 * 36px control. `gate` is the one variant that wears the accent as a fill: it is
 * reserved for the primary move at a human gate. Everything else is a hairline
 * or nothing at all, and `danger` is text-only until you hover it, so a
 * destructive verb never shouts from a resting row.
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md',
    'text-sm font-medium transition-[background-color,border-color,color,opacity] duration-100',
    'disabled:pointer-events-none disabled:opacity-45',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[13px]",
    'outline-none',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-accent-hover',
        gate: [
          'text-white bg-[image:var(--dy-grad-gate)] shadow-[inset_0_1px_0_rgb(255_255_255/18%)]',
          'hover:brightness-110',
        ].join(' '),
        /**
         * The one call to action a page is allowed. New job, new device: the
         * verb a person came to the page to perform, drawn in the full brand
         * gradient with a coloured lift under it so it is found before the page
         * is read. Pair it with `size="pill"`; anything smaller wastes it.
         */
        hero: [
          'text-white bg-[image:var(--dy-grad-x)] bg-[length:180%_100%] bg-left',
          'shadow-[inset_0_1px_0_rgb(255_255_255/22%),0_4px_14px_-4px_color-mix(in_srgb,var(--dy-accent)_58%,transparent)]',
          'transition-[background-position,box-shadow,filter] duration-200 hover:bg-right hover:brightness-105',
        ].join(' '),
        outline: 'border border-line-2 bg-transparent text-foreground hover:bg-raised',
        subtle: 'bg-raised text-foreground hover:bg-line',
        ghost: 'text-fg-2 hover:bg-raised hover:text-foreground',
        danger: 'text-bad hover:bg-bad-bg hover:text-bad',
        destructive: 'bg-destructive text-white hover:opacity-90',
        link: 'text-primary underline-offset-2 hover:underline',
      },
      size: {
        default: 'h-8 px-3',
        sm: 'h-7 px-2.5',
        xs: 'h-6 rounded-sm px-2 text-xs',
        lg: 'h-9 px-4',
        /** The page's primary verb: tall enough and round enough to be found. */
        pill: "h-9 rounded-full px-4 text-base [&_svg:not([class*='size-'])]:size-4",
        'pill-sm': 'h-8 rounded-full px-3.5',
        icon: 'size-8',
        'icon-sm': 'size-7',
        'icon-xs': 'size-6 rounded-sm',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
